import type { DocumentItem } from "@/types/ui-models";
import { NoRealData } from "@/components/shared";
import { daysToExpiry, fmtDate, dash } from "../lib/docUtils";

interface ExpirationPanelProps {
  documents: DocumentItem[];
  onViewDocument: (id: string) => void;
}

type Priority = "critico" | "atencao" | "cautela";

/** Faixas de alerta calculadas a partir da data de validade real de cada documento. */
function priorityOf(days: number): Priority | null {
  if (days < 0) return "critico";
  if (days <= 30) return "atencao";
  if (days <= 60) return "cautela";
  return null;
}

const priorityConfig: Record<Priority, { bg: string; border: string; text: string; iconBg: string; iconText: string; label: string }> = {
  critico: { bg: "bg-danger-50", border: "border-danger-200", text: "text-danger-700", iconBg: "bg-danger-100", iconText: "text-danger-600", label: "Vencido" },
  atencao: { bg: "bg-warning-50", border: "border-warning-200", text: "text-warning-700", iconBg: "bg-warning-100", iconText: "text-warning-600", label: "Até 30 dias" },
  cautela: { bg: "bg-caution-50", border: "border-caution-200", text: "text-caution-700", iconBg: "bg-caution-100", iconText: "text-caution-600", label: "31 a 60 dias" },
};

interface ExpirationEntry {
  doc: DocumentItem;
  days: number;
  priority: Priority;
}

export default function ExpirationPanel({ documents, onViewDocument }: ExpirationPanelProps) {
  const withExpiry = documents.filter((d) => daysToExpiry(d) !== null);
  const items: ExpirationEntry[] = [];
  for (const doc of withExpiry) {
    const days = daysToExpiry(doc) as number;
    const priority = priorityOf(days);
    if (priority) items.push({ doc, days, priority });
  }
  items.sort((a, b) => a.days - b.days);

  if (withExpiry.length === 0) {
    return (
      <NoRealData
        title="Nenhum documento com validade informada"
        description="O controle de vencimentos é calculado a partir da data de validade registrada em cada documento."
        icon="ri-calendar-close-line"
      />
    );
  }

  const count = (p: Priority) => items.filter((i) => i.priority === p).length;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-foreground-900">Painel de Controle de Vencimentos</h3>
        <p className="text-xs text-foreground-500 mt-1">{withExpiry.length} documento(s) com data de validade informada.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-danger-50 rounded-xl border border-danger-200 p-4 text-center">
          <div className="text-2xl font-bold text-danger-700 font-heading">{count("critico")}</div>
          <div className="text-xs text-danger-600 mt-1">Documentos vencidos</div>
        </div>
        <div className="bg-warning-50 rounded-xl border border-warning-200 p-4 text-center">
          <div className="text-2xl font-bold text-warning-700 font-heading">{count("atencao")}</div>
          <div className="text-xs text-warning-600 mt-1">A vencer em até 30 dias</div>
        </div>
        <div className="bg-caution-50 rounded-xl border border-caution-200 p-4 text-center">
          <div className="text-2xl font-bold text-caution-700 font-heading">{count("cautela")}</div>
          <div className="text-xs text-caution-600 mt-1">A vencer em 31 a 60 dias</div>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-foreground-500 text-center py-6">Nenhum documento vencido ou a vencer nos próximos 60 dias.</p>
      ) : (
        <div className="space-y-2">
          {items.map(({ doc, days, priority }) => {
            const p = priorityConfig[priority];
            const daysLabel = days < 0 ? `${Math.abs(days)} dias vencido` : days === 0 ? "Vence hoje" : `${days} dias restantes`;
            return (
              <button
                key={doc.id}
                onClick={() => onViewDocument(doc.id)}
                className={`w-full text-left flex items-center justify-between gap-4 p-3 rounded-lg border ${p.bg} ${p.border}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 rounded-lg ${p.iconBg} flex items-center justify-center shrink-0`}>
                    <i className={`ri-error-warning-line ${p.iconText} w-full h-full flex items-center justify-center text-sm`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground-800 truncate">{doc.title}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-foreground-500">{doc.type}</span>
                      <span className="text-xs text-foreground-400">|</span>
                      <span className="text-xs text-foreground-500">{dash(doc.primaryBinding || doc.projectName)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div className={`text-xs font-semibold ${p.text}`}>{daysLabel}</div>
                    <div className="text-[10px] text-foreground-400">{fmtDate(doc.expiryDate)}</div>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${p.text} ${p.iconBg}`}>{p.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
